import { Link } from 'react-router-dom';
import { formatDateTime } from '../../utils/format';
import { actorLabel, humanize } from './investorLabels';

// Fields that only repeat what the row already shows (actor, package, ids).
const HIDDEN_DETAIL_KEYS = new Set([
  'actor_org_id',
  'actor_user_id',
  'package_id',
  'correlation_id',
]);

/** Some events store a JSON payload as their message; surface it as fields. */
export function parseEventDetails(message) {
  if (typeof message !== 'string') return null;
  const trimmed = message.trim();
  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) return null;
  try {
    const parsed = JSON.parse(trimmed);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function formatDetailValue(value) {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function EventMessage({ message }) {
  const details = parseEventDetails(message);
  if (!details) {
    return <p className="mt-0.5 break-words text-sm text-eorig-ink">{message}</p>;
  }
  const entries = Object.entries(details).filter(([key]) => !HIDDEN_DETAIL_KEYS.has(key));
  if (!entries.length) return null;
  return (
    <dl className="mt-1 grid gap-x-4 gap-y-1 text-xs sm:grid-cols-2 lg:grid-cols-3">
      {entries.map(([key, value]) => (
        <div key={key} className="min-w-0">
          <dt className="text-[10px] font-semibold uppercase tracking-wide text-eorig-muted">
            {humanize(key)}
          </dt>
          <dd className="break-all text-eorig-ink">{formatDetailValue(value)}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Who did what, when — one row per ActivityEvent, newest first.
 * Shows only what the backend recorded; missing actor/org render as "—".
 */
export function ActivityTimeline({ items = [], emptyMessage = 'No activity recorded.', showPackage = true }) {
  if (!items.length) {
    return <p className="text-xs text-eorig-muted">{emptyMessage}</p>;
  }
  return (
    <ol className="relative min-w-0 space-y-4 border-l border-eorig-border pl-4">
      {items.map((event) => (
        <li key={event.id} className="relative min-w-0">
          <span
            className="absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full bg-eorig-gradient ring-2 ring-white"
            aria-hidden
          />
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            <span className="text-sm font-semibold text-eorig-ink">{humanize(event.event_type)}</span>
            <time className="text-[11px] text-eorig-muted" dateTime={event.created_at}>
              {formatDateTime(event.created_at)}
            </time>
          </div>
          {event.message ? <EventMessage message={event.message} /> : null}
          <p className="mt-0.5 break-words text-[11px] text-eorig-muted">
            <span className="font-semibold text-eorig-ink">{actorLabel(event)}</span>
            {event.actor_email && event.actor_name ? ` (${event.actor_email})` : ''}
            {event.organization_name ? ` · ${event.organization_name}` : ''}
            {showPackage && event.package_id ? (
              <>
                {' · '}
                <Link
                  to={`/investor/packages/${event.package_id}`}
                  className="font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
                >
                  {event.package_reference || 'package'}
                </Link>
              </>
            ) : null}
          </p>
        </li>
      ))}
    </ol>
  );
}
